/**
 * Class description
 *
 * Created: 2019-08-13
 * @author jonas
 */

import {BasicBitmapCountUp} from "../display/BasicBitmapCountUp";
import {HorribleHeroesTextStyles} from "../config/HorribleHeroesTextStyles";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Bounce, Ease, Linear, TimelineLite, TweenLite} from "gsap";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {HorribleHeroesLayerConfig} from "../config/IHorribleHeroesLayerConfig";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {HorribleHeroesGameModuleConfig} from "../HorribleHeroesGameModuleConfig";
import {HorribleHeroesParsedGameData} from "../server/data/HorribleHeroesParsedGameData";
import {SoundManager} from "../resource/sound/SoundManager";
import {HorribleHeroesSoundConfig} from "../HorribleHeroesSoundConfig";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";

export class CountUpView extends BaseView {
    private _layer: ScalableLayer;
    private _bitmapText: BasicBitmapCountUp;
    private resizeData: IResizeData;

    constructor() {
        super();
        this._layer = StageManager.getLayer(HorribleHeroesLayerConfig.WIN_COUNT_UP.name);
    }

    // #362
    private static playEndSound(type: WinRatioType): void {
        if (type === WinRatioType.TINY) {
            SoundManager.playCountUpEndsTiny();
        } else if (type === WinRatioType.SMALL) {
            SoundManager.playCountUpEndsSmall();
        } else if (type === WinRatioType.MEDIUM) {
            SoundManager.playCountUpEndsMedium();
        } else if (type === WinRatioType.LARGE) {
            SoundManager.playCountUpEndsLarge();
        }
    }

    public getWinStepAnimation(from: number, to: number, config: IWinRatioConfig, ease: Ease = Linear.easeNone, isBigWin: boolean = false): TimelineLite {
        this.positionBitmapText(isBigWin);
        this._layer.addChild(this);
        this.alpha = 0;

        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            if (isBigWin) {
                this.scaleUpBigWin();
            }

            // #350
            if (config.duration > 0) {
                TweenLite.to(this, 0.3, {alpha: 1});

                // #362, #444
                if (!isBigWin) {
                    SoundManager.playCountUp();
                }
            } else {
                this.alpha = 1;
            }

            this._bitmapText.getCountAnimation(from, to, config!.duration, ease);
        });

        tl.add(() => {
            this._bitmapText.pivot.set(0);
            if (config.type === "tiny" || config.type === "small") {
                TweenLite.fromTo(this._bitmapText.scale, 0.1, {x: 0.8, y: 0.8}, {
                    x: 1.2, y: 1.2, onComplete: () => {
                        TweenLite.to(this._bitmapText.scale, 0.1, {
                            x: 1.15, y: 1.15, onComplete: () => {
                                TweenLite.to(this._bitmapText.scale, 0.1, {x: 1.1, y: 1.1, ease: Bounce.easeIn});
                            }
                        });
                    }
                });
            }
        }, config!.duration);

        return tl;
    }

    public abort(value: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => this._bitmapText.stopAt(value));
        return tl;
    }

    public end(value: number, displayDuration: number, fadeDuration: number, data: HorribleHeroesParsedGameData, type: WinRatioType | undefined): TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => {
            this._layer.addChild(this);

            if (data.isBigWin) {
                TweenLite.to(this._bitmapText, 0.2, {
                    y: 360, ease: Linear.easeIn, onComplete: () => this.scaleUpBigWin()
                });
            } else {
                // #582
                if (!HorribleHeroesGameConfig.instance.WIN_BELOW_STAKE) {
                    // #362
                    type = type ? type : WinRatioType.TINY;
                    CountUpView.playEndSound(type);
                }
            }
            SoundManager.stopSound(HorribleHeroesSoundConfig.instance.COUNT_UP);

            this.positionBitmapText(data.isBigWin);
            this.alpha = 1;
            this._bitmapText.stopAt(value);
            SlotGame.winFieldController.showWinField();
        });

        tl.add(() => this.fadeAndRemove(fadeDuration), displayDuration);

        return tl;
    }

    public hideCountUp(): void {
        this._layer.removeChild(this);
    }

    public onResize(data: IResizeData): void {
        this.resizeData = data;
        const posY: number = data.bottom - 100;
        this._bitmapText.position.set(360, (posY > 520) ? 520 : posY);
    }

    protected initAnimations(): void {
        this._bitmapText = new BasicBitmapCountUp(HorribleHeroesTextStyles.WIN_COUNT_UP, GameConfig.instance.REEL_AREA_WIDTH, new PIXI.Point(0.5, 0.5));
        this._bitmapText.position.set(GameConfig.instance.REEL_AREA_WIDTH * 0.5 + GameConfig.instance.REEL_AREA_POS_X, GameConfig.instance.REEL_AREA_HEIGHT * 0.5 + GameConfig.instance.REEL_AREA_POS_Y);
        this.addChild(this._bitmapText);
    }

    private scaleUpBigWin(): void {
        TweenLite.fromTo(this._bitmapText.scale, 0.1, {x: 0.8, y: 0.8}, {
            x: 1.2, y: 1.2, ease: Linear.easeOut, onComplete: () => {
                TweenLite.to(this._bitmapText.scale, 0.05, {x: 1.1, y: 1.1, ease: Bounce.easeIn});
            }
        });
    }

    private fadeAndRemove(fadeDuration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            HorribleHeroesGameModuleConfig.winTextView.hideCountUp(); // this will fade out the "waysContainer"

            TweenLite.to(this, fadeDuration, {
                alpha: 0,
                ease: Linear.easeOut,
                onComplete: () => this._layer.removeChild(this)
            });
        });
        //child wll  be  removed after 0.2 secs fadeIn completed
        return tl;
    }

    // #318 -> 1st point
    private positionBitmapText(isBigWin: boolean): void {
        const posY: number = this.resizeData.bottom - 100;
        if (isBigWin) {
            this._bitmapText.position.set(360, (posY > 520) ? 520 : posY);
        } else {
            this._bitmapText.position.set(360);
            /*to make it in center while normal initial win*/
        }
    }
}